import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Users,
  FileText,
  DollarSign,
  Coins,
  Calendar,
  Building2,
  UserCircle,
} from 'lucide-react'
import { useContentControls, CC_FIELD_TAGS } from '../../hooks/useContentControls'

/** Available fields to insert as Content Controls */
const FIELDS = [
  { tag: CC_FIELD_TAGS.counterparty, icon: Users, titleKey: 'contentControls.counterparty', placeholderKey: 'contentControls.counterpartyPlaceholder' },
  { tag: CC_FIELD_TAGS.document_type, icon: FileText, titleKey: 'contentControls.documentType', placeholderKey: 'contentControls.documentTypePlaceholder' },
  { tag: CC_FIELD_TAGS.contract_value, icon: DollarSign, titleKey: 'contentControls.contractValue', placeholderKey: 'contentControls.contractValuePlaceholder' },
  { tag: CC_FIELD_TAGS.contract_currency, icon: Coins, titleKey: 'contentControls.currency', placeholderKey: 'contentControls.currencyPlaceholder' },
  { tag: CC_FIELD_TAGS.contract_expires_at, icon: Calendar, titleKey: 'contentControls.expiryDate', placeholderKey: 'contentControls.expiryDatePlaceholder' },
  { tag: CC_FIELD_TAGS.department, icon: Building2, titleKey: 'contentControls.department', placeholderKey: 'contentControls.departmentPlaceholder' },
  { tag: CC_FIELD_TAGS.responsible_person, icon: UserCircle, titleKey: 'contentControls.responsiblePerson', placeholderKey: 'contentControls.responsiblePersonPlaceholder' },
] as const

/**
 * Dropdown menu for inserting KobSign Content Controls into the Word document.
 * Each item inserts a tagged CC at the current cursor position.
 */
export function InsertFieldMenu() {
  const { t } = useTranslation()
  const { insertField, isInserting } = useContentControls()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleInsert = async (field: typeof FIELDS[number]) => {
    await insertField(field.tag, t(field.titleKey), t(field.placeholderKey))
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isInserting}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border border-primary-200 dark:border-primary-800 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors disabled:opacity-50"
        title={t('contentControls.insertField')}
      >
        <Plus size={10} />
        {t('contentControls.insertInDoc')}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-50 py-1">
          <div className="px-2 py-1.5 text-[10px] font-medium text-neutral-400 dark:text-neutral-500 uppercase tracking-wide">
            {t('contentControls.insertField')}
          </div>
          {FIELDS.map((field) => {
            const Icon = field.icon
            return (
              <button
                key={field.tag}
                onClick={() => handleInsert(field)}
                disabled={isInserting}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors disabled:opacity-50"
              >
                <Icon size={12} className="shrink-0 text-primary-500" />
                <span className="text-neutral-900 dark:text-neutral-100">
                  {t(field.titleKey)}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
